/**
 * Constants for SeedInventory component
 */

// All possible categories with their metadata
export const ALL_CATEGORIES = [
  { id: 'vegetable', label: 'Vegetable' },
  { id: 'fruit', label: 'Fruit' },
  { id: 'herb', label: 'Herb' },
  { id: 'flower', label: 'Flower' },
  { id: 'fungi', label: 'Fungi' },
  { id: 'nut', label: 'Nut' }
];

// Category options that vary by plant type
export const CATEGORIES_BY_TYPE = {
  seed: ['vegetable', 'fruit', 'herb', 'flower', 'fungi'],
  perennial: ['fruit', 'herb', 'flower'],
  tree: ['fruit', 'nut'],
  bush: ['fruit', 'herb']
};

// Plant types
export const TYPES = [
  { id: 'seed', label: 'Seed' },
  { id: 'perennial', label: 'Perennial' },
  { id: 'tree', label: 'Tree' },
  { id: 'bush', label: 'Bush' }
];

// Harvest season options for perennials/trees/bushes
export const HARVEST_SEASONS = [
  { id: 'spring', label: 'Spring' },
  { id: 'summer', label: 'Summer' },
  { id: 'fall', label: 'Fall' },
];

// Legacy constant for backwards compatibility with filters
export const CATEGORIES = ALL_CATEGORIES;

// Empty batch entry template
export const createEmptyBatchEntry = () => ({
  plant: '',
  variety: '',
  quantity: '',
  season: '',
  category: '',
  need_to_buy: false,
  notes: '',
  type: 'seed',
  days_to_production: '',
  weeks_indoors_before_transplant: '',
  preferred_start_method: '',
  harvest_season: [],
  years_to_production: '',
  planned_start_date: '',
  planned_transplant_date: '',
  frost_tolerant: false,
  spacing: '',
  sun_requirement: '',
  water_needs: '',
  direct_sow_weeks_before_last_frost: ''
});

// Empty form data template
export const createEmptyFormData = () => ({
  plant: '',
  variety: '',
  quantity: '',
  season: '',
  category: '',
  need_to_buy: false,
  notes: '',
  type: 'seed',
  days_to_production: '',
  weeks_indoors_before_transplant: '',
  preferred_start_method: '',
  harvest_season: [],
  years_to_production: '',
  planned_start_date: '',
  planned_transplant_date: '',
  frost_tolerant: false,
  spacing: '',
  icon: '',
  sun_requirement: '',
  water_needs: '',
  direct_sow_weeks_before_last_frost: ''
});

// Get available categories for a given plant type
export const getCategoryOptions = (plantType) => {
  const categoryIds = CATEGORIES_BY_TYPE[plantType] || CATEGORIES_BY_TYPE.seed;
  return ALL_CATEGORIES.filter(cat => categoryIds.includes(cat.id));
};

// Get form configuration based on plant type and category
export const getFormConfig = (plantType, category) => {
  const isAnnual = plantType === 'seed';
  return {
    showSeason: isAnnual,
    showDaysToProduction: isAnnual,
    showPreferredStartMethod: isAnnual,
    showWeeksIndoors: isAnnual,
    showYearsToProduction: !isAnnual,
    productionLabel: category === 'flower' ? 'Days to first bloom' : 'Days to first harvest',
    yearsLabel: category === 'flower' ? 'Years to first bloom' : 'Years to first harvest'
  };
};

// Reset type-specific fields when plant type changes
export const getResetFieldsForType = (newType, currentData) => {
  const isAnnual = newType === 'seed';

  // Check if current category is valid for the new type
  const validCategories = CATEGORIES_BY_TYPE[newType] || [];
  const categoryValid = validCategories.includes(currentData.category);

  if (isAnnual) {
    // Switching to annual - clear perennial fields (keep harvest_season — shared field)
    return {
      years_to_production: '',
      category: categoryValid ? currentData.category : ''
    };
  } else {
    // Switching to perennial/tree/bush - clear annual fields
    return {
      season: '',
      days_to_production: '',
      weeks_indoors_before_transplant: '',
      preferred_start_method: '',
      planned_start_date: '',
      planned_transplant_date: '',
      category: categoryValid ? currentData.category : ''
    };
  }
};
